import React, { useState, useRef, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Button } from './Button';
import { Camera, X, RotateCcw } from 'lucide-react';

interface BarcodeScannerProps {
  onScan: (result: string) => void;
  onClose: () => void;
  isOpen: boolean;
  title?: string;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  onClose,
  isOpen,
  title = 'Scan Barcode'
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const elementId = 'qr-code-scanner';

  useEffect(() => {
    if (isOpen && !isScanning) {
      startScanner();
    }

    return () => {
      if (scannerRef.current) {
        try {
          scannerRef.current.clear();
        } catch (err) {
          console.log('Scanner cleanup error:', err);
        }
      }
    };
  }, [isOpen]);

  const startScanner = () => {
    setError(null);
    setIsScanning(true);

    try {
      const scanner = new Html5QrcodeScanner(
        elementId,
        {
          fps: 10,
          qrbox: {
            width: 250,
            height: 250,
          },
          rememberLastUsedCamera: true,
          supportedScanTypes: [],
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
        },
        false
      );

      scanner.render(
        (decodedText: string) => {
          // Success callback
          scanner.clear();
          setIsScanning(false);
          onScan(decodedText);
          onClose();
        },
        (error: any) => {
          // Error callback - don't show errors for scanning attempts
          if (error && !error.includes('No MultiFormat Readers were able to detect the code')) {
            console.log('Scan error:', error);
          }
        }
      );

      scannerRef.current = scanner;
    } catch (err) {
      console.error('Scanner initialization error:', err);
      setError('Failed to initialize camera. Please ensure camera permissions are granted.');
      setIsScanning(false);
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.log('Stop scanner error:', err);
      }
    }
    setIsScanning(false);
  };

  const restartScanner = () => {
    stopScanner();
    setTimeout(() => {
      startScanner();
    }, 500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-8 border max-w-4xl shadow-xl rounded-xl bg-white dark:bg-gray-800">
        <div className="mt-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-medium text-gray-900 dark:text-gray-100 flex items-center">
              <Camera className="h-7 w-7 mr-3" />
              {title}
            </h3>
            <button
              onClick={() => {
                stopScanner();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-7 w-7" />
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-base text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Scanner Container */}
          <div className="mb-8">
            <div 
              id={elementId} 
              className="w-full max-w-2xl mx-auto border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl overflow-hidden"
            />
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={restartScanner}
              variant="secondary"
              className="flex-1"
              disabled={!isScanning}
              size="lg"
            >
              <RotateCcw className="h-5 w-5 mr-3" />
              Restart Camera
            </Button>
            <Button
              onClick={() => {
                stopScanner();
                onClose();
              }}
              variant="secondary"
              className="flex-1"
              size="lg"
            >
              Cancel
            </Button>
          </div>

          {/* Instructions */}
          <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <h4 className="text-base font-medium text-blue-800 dark:text-blue-300 mb-3">
              Scanning Instructions:
            </h4>
            <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-2">
              <li>• Position the barcode clearly within the scanning area</li>
              <li>• Ensure good lighting for best results</li>
              <li>• Hold steady until the code is detected</li>
              <li>• Works with most standard barcode formats</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};